import { useState } from "react";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { formatDateOnly } from "../../services/hiveActions";

/**
 * "Did this happen once or twice?" (#272)
 *
 * Deliberately NOT its own page with its own nav entry. These are rare --
 * most accounts will never see one -- and a permanent link to a screen
 * that is empty 99% of the time is clutter that teaches people to ignore
 * it. It appears on the dashboard when there is something to answer, and
 * vanishes when there is not.
 *
 * The framing is the feature. The question is never "which row do you
 * want to keep": it is what actually happened in the yard. If the answer
 * is "twice", every row stays and the double dose becomes something to
 * look at -- because a colony that got two doses got two doses, whatever
 * anyone later does to the database.
 */

export function DuplicateQuestions({ suspicions = [], onResolve, busyId = null }) {
  const [expanded, setExpanded] = useState(null);
  /*
   * Which entries the user has ticked as being the same event, per
   * suspicion. Only used where a window holds THREE or more.
   *
   * Reported from dev: three entries offered only "keep all 3" or "keep
   * 1". Both can be wrong at once -- two of them one extraction entered
   * twice, the third a real second super off the same hive that
   * afternoon. Neither answer was true, and answering either put a wrong
   * number in the honey pool.
   *
   * Two entries keeps its single click. Turning the commonest case into
   * a tick-boxes exercise to serve the rarer one would make the whole
   * panel feel like work, and this panel only earns its place by being
   * quick.
   */
  const [chosen, setChosen] = useState({});

  function toggle(suspicionId, logId) {
    setChosen((c) => {
      const current = c[suspicionId] || [];
      return {
        ...c,
        [suspicionId]: current.includes(logId)
          ? current.filter((id) => id !== logId)
          : [...current, logId],
      };
    });
  }

  if (!suspicions.length) return null;

  return (
    <Card
      title={
        suspicions.length === 1
          ? "One entry needs checking"
          : `${suspicions.length} entries need checking`
      }
      className="section-spacing"
    >
      <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
        The same thing looks like it was logged twice on one hive. Worth a
        moment — it does not mean anyone did anything wrong.
      </p>

      {suspicions.map((s) => {
        const isOpen = expanded === s.suspicion_id;
        const busy = busyId === s.suspicion_id;
        const isTreatment = s.entity_type === "Treatment";
        const isHarvest = s.entity_type === "Harvest";
        // One person logging twice and two people logging once are the
        // same question with different causes, and the wording has to
        // match or it reads as an accusation about the wrong thing.
        const people = new Set((s.entries || []).map((e) => e.applied_by));
        const samePerson = people.size === 1;
        // Three or more is where "all of them or none of them" stops
        // being able to describe what happened.
        const needsPicking = (s.entries || []).length > 2;
        const picked = chosen[s.suspicion_id] || [];

        return (
          <div
            key={s.suspicion_id}
            style={{
              borderTop: "1px solid var(--color-border)",
              padding: "var(--space-3) 0",
            }}
          >
            <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "baseline", flexWrap: "wrap" }}>
              <strong>Hive {s.hive_number}</strong>
              <Badge tone={isTreatment ? "warning" : "neutral"}>
                {s.product_name || s.entity_type}
              </Badge>
            </div>

            <p style={{ margin: "var(--space-2) 0 0", fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              {samePerson
                ? "Logged twice by the same person — easily done if you weren't sure the first one saved."
                : "Logged by two different people, which happens when you're both working offline."}
            </p>

            <ul style={{
              margin: "var(--space-2) 0",
              paddingLeft: needsPicking && isOpen ? 0 : "1.2rem",
              listStyle: needsPicking && isOpen ? "none" : undefined,
            }}>
              {(s.entries || []).map((e) => (
                <li key={e.log_id}>
                  {/* Who and when, because the next step is usually
                      asking them -- which is only possible now that
                      entries record a person (#268). */}
                  {needsPicking && isOpen ? (
                    <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        className="touch-target"
                        checked={picked.includes(e.log_id)}
                        onChange={() => toggle(s.suspicion_id, e.log_id)}
                      />
                      <span>
                        {e.applied_by_label || "Someone on your team"} — {formatDateOnly(e.entry_date)}
                      </span>
                    </label>
                  ) : (
                    <>{e.applied_by_label || "Someone on your team"} — {formatDateOnly(e.entry_date)}</>
                  )}
                </li>
              ))}
            </ul>

            {!isOpen ? (
              <Button size="sm" variant="secondary" onClick={() => setExpanded(s.suspicion_id)}>
                Sort this out
              </Button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                <Button
                  size="sm"
                  // Needs two ticked before it can mean anything. The
                  // server refuses fewer, and a button that produces an
                  // error is worse than one that waits.
                  disabled={busy || (needsPicking && picked.length < 2)}
                  onClick={() => onResolve(
                    s.suspicion_id, "same_event",
                    needsPicking ? picked : undefined,
                  )}
                >
                  {needsPicking
                    ? `The ticked ones are the same entry${picked.length ? ` (${picked.length})` : ""}`
                    : "It happened once — this was entered twice"}
                </Button>
                {needsPicking && (
                  <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                    Three or more here, so tick the ones that are the same
                    entry. Anything left unticked stays exactly as it is —
                    two of these can be one extraction logged twice while
                    the third is a real second super.
                  </p>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => onResolve(s.suspicion_id, "both_real")}
                >
                  {/* "Twice" is literally wrong once a window holds
                      three, and this is the answer that keeps every row
                      -- so it has to name how many rows that is. */}
                  {needsPicking
                    ? `They all really happened (${(s.entries || []).length})`
                    : "It really happened twice"}
                </Button>
                {/* Said BEFORE they choose, not after. This is the
                    answer with a consequence, and hiding it until
                    afterwards would make the safe-looking option the one
                    people click. Every entity that HAS a consequence
                    says its own -- a harvest one was missing when
                    harvests joined this panel (V092), which would have
                    left the honey pool as the one thing on the page
                    nobody was warned about. */}
                {isTreatment && (
                  <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                    If it really happened twice, the colony was treated twice.
                    Both entries stay, and we&apos;ll raise it for someone to
                    check the withdrawal period before any honey is sold.
                  </p>
                )}
                {isHarvest && (
                  <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                    If it really came off twice, both harvests stay and the
                    honey pool keeps both amounts. If it was entered twice,
                    the duplicate&apos;s honey comes back out of the pool.
                  </p>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => onResolve(s.suspicion_id, "not_related")}
                >
                  {needsPicking
                    ? "These are all different things — leave them alone"
                    : "These are different things — leave both alone"}
                </Button>
              </div>
            )}
          </div>
        );
      })}

      <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", marginBottom: 0 }}>
        {/* "your history", not "your treatment history": harvests are in
            this panel too since V092, and a beekeeper reading it about a
            double-counted extraction would rightly wonder what treatments
            had to do with it. */}
        Nothing here deletes an entry. Whichever you choose, both records stay
        in your history.
      </p>
    </Card>
  );
}
